import {
  deleteClubRankingImport,
  getClubRankingImports,
} from '@/api/category-ranking/categoryRankingApi';
import type { RankingImportBatch } from '@/api/category-ranking/types';
import { Button } from '@/components/ui/button';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import type { Sport } from '@/types/sports.model';
import { handleGlobalError } from '@/utils/error-handler';
import { formatDateForUser } from '@/utils/formatters';
import { LoaderCircle, Trash2 } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';

interface RankingImportBatchesModalProps {
  clubId: string;
  /** Used to show the sport's name: the batch carries its uid ("PADEL"). */
  sports: Sport[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a delete so the panel refetches the ranking. */
  onChanged: () => void;
}

export const RankingImportBatchesModal: React.FC<
  RankingImportBatchesModalProps
> = ({ clubId, sports, open, onOpenChange, onChanged }) => {
  const [batches, setBatches] = useState<RankingImportBatch[] | null>(null);
  const [batchToDelete, setBatchToDelete] = useState<RankingImportBatch | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setBatches(null);
    getClubRankingImports(clubId)
      .then((data) => {
        if (!cancelled) setBatches(data);
      })
      .catch((error) => {
        if (cancelled) return;
        setBatches([]);
        handleGlobalError(
          error,
          'Error',
          'No se pudieron cargar las importaciones del club.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [open, clubId, reloadKey]);

  const sportName = (uid: string): string =>
    sports.find((sport) => sport.uid === uid)?.name ?? uid;

  const confirmDelete = async () => {
    if (!batchToDelete) return;
    setDeleting(true);
    try {
      await deleteClubRankingImport(clubId, batchToDelete.id);
      toast({
        title: 'Importación eliminada',
        description: `Se revirtieron los puntos de «${batchToDelete.tournamentName}».`,
      });
      setBatchToDelete(null);
      setReloadKey((key) => key + 1);
      onChanged();
    } catch (error) {
      handleGlobalError(
        error,
        'Error',
        'No se pudo eliminar la importación.',
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Same guard as RankingImportModal: a delete mutates data, so closing mid-request is
        // blocked until it settles instead of leaving the batch in a half-reverted state.
        if (next || deleting) return;
        onOpenChange(false);
      }}
    >
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importaciones del club</DialogTitle>
          <DialogDescription>
            Cada lote agrupa los puntos que se cargaron juntos. Eliminarlo
            revierte esos puntos.
          </DialogDescription>
        </DialogHeader>

        {batches === null ? (
          <div className="flex justify-center py-10">
            <LoaderCircle className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : batches.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">
            Este club todavía no tiene importaciones.
          </p>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200">
            {batches.map((batch, index) => (
              <div
                key={batch.id}
                className={`flex items-start justify-between gap-3 px-4 py-3 ${
                  index > 0 ? 'border-t border-gray-200' : ''
                }`}
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-800">
                    {batch.tournamentName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {sportName(batch.sport)} ·{' '}
                    {formatDateForUser(batch.earnedAt)}
                  </p>
                  <p className="mt-1 text-xs text-gray-600">
                    {batch.rowsCount} filas · {batch.totalPoints} pts ·{' '}
                    {batch.createdUsersCount} cuentas creadas
                  </p>
                  <p className="text-xs text-gray-400">
                    Importado por {batch.importedByName ?? 'un manager'} el{' '}
                    {formatDateForUser(batch.createdAt)}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Eliminar ${batch.tournamentName}`}
                  onClick={() => setBatchToDelete(batch)}
                >
                  <Trash2 size={16} className="text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>

      <ConfirmationModal
        isOpen={batchToDelete !== null}
        onClose={() => (deleting ? undefined : setBatchToDelete(null))}
        onConfirm={() => void confirmDelete()}
        title="Eliminar importación"
        description={`Esto revierte todos los puntos del lote «${batchToDelete?.tournamentName ?? ''}». Las cuentas que se crearon con el lote no se borran.`}
        confirmText="Eliminar"
      />
    </Dialog>
  );
};
